import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Avatar as PrimeAvatar} from "primereact/avatar";
import {Badge} from "primereact/badge";
import {Button} from "primereact/button";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {ContextMenu} from "primereact/contextmenu";
import {Dialog} from "primereact/dialog";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {MultiSelect} from "primereact/multiselect";
import {ProgressSpinner} from "primereact/progressspinner";
import {TabPanel, TabView} from "primereact/tabview";
import {Dropdown} from "primereact/dropdown";
import {Tag} from "primereact/tag";
import {Toast} from "primereact/toast";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import Span from "../../../system/components/span";
import TwemojiText from "../../../system/components/twemoji-text";
import {useChat} from "../../../models/chat/use-chat";
import {useChatParticipant} from "../../../models/chat-participant/use-chat-participant";
import {useChatMessage} from "../../../models/chat-message/use-chat-message";
import {useDeliverable} from "../../../models/deliverable/use-deliverable";
import {useGoal} from "../../../models/goal/use-goal";
import {useProject} from "../../../models/project/use-project";
import {useTask} from "../../../models/task/use-task";
import {useTeam} from "../../../models/team/use-team";
import {useUserAccess} from "../../../models/user-access/use-user-access";
import {setWorkspaceMembership} from "../../../system/app-slice";
import {copyToClipboard} from "../../../system/utils";
import {
    mergeWorkspaceTasks,
    setWorkspaceGraphGoals,
    setWorkspaceGraphProjects,
    setWorkspaceGraphTeams,
    upsertWorkspaceGoal,
    upsertWorkspaceProject,
    upsertWorkspaceTask,
} from "../../../system/workspace-graph-slice";
import GoalSidebar from "../../goals/components/goal-sidebar";
import CreateProjectDialog from "../../projects/components/create-project-dialog";
import DeliverableSidebar from "../../projects/components/deliverable-sidebar";
import ProjectSidebar from "../../projects/components/project-sidebar";
import TaskSidebar from "../../projects/components/task-sidebar";
import DeliverableDialog from "../../tasks/deliverable-dialog";
import TaskDialog from "../../tasks/task-dialog";
import ChatMessage from "../chat-message";
import {
    nextReactionEmoji,
    QUICK_REACTIONS,
    reactionActorName,
} from "../chat-message-reactions";
import UploadDialog from "../../components/upload-dialog";
import ChatComposer, {
    buildReferenceSuggestions,
    mentionToken,
    referenceToken,
    valueList,
} from "../chat-composer";
import {entityKey, sameEntity} from "../inbox-utils";
import {useTranslate} from "../../../system/useTranslate";
import {chatMessageAttachmentIdentifiers} from "../chat-message-attachment-payload";
import {
    groupMessagesByPersianDay,
    sortMessagesChronologically,
} from "../conversation-message-utils";


// conversation header filters (spec section 4)
const MESSAGE_KIND_OPTIONS = [
    {label: "فایل‌ها", value: "files"},
    {label: "کارها", value: "task"},
    {label: "پروژه‌ها", value: "project"},
    {label: "اهداف", value: "goal"},
];
const MESSAGE_DATE_OPTIONS = [
    {label: "امروز", value: "today"},
    {label: "دیروز", value: "yesterday"},
    {label: "۷ روز اخیر", value: "week"},
    {label: "۳۰ روز اخیر", value: "month"},
];

export function messageDateRangeFor(value, now = new Date()) {
    if (!value) return null;
    const startOfDay = (offsetDays) => {
        const date = new Date(now);
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() - offsetDays);
        return date;
    };
    if (value === "today") return {since: startOfDay(0).toISOString(), until: null};
    if (value === "yesterday") return {since: startOfDay(1).toISOString(), until: startOfDay(0).toISOString()};
    if (value === "week") return {since: startOfDay(6).toISOString(), until: null};
    if (value === "month") return {since: startOfDay(29).toISOString(), until: null};
    return null;
}

function getOtherUser(chat, currentUser) {
    return (chat?.users || []).find((membership) => (
        String(membership?.member?.id) !== String(currentUser?.id)
    ))?.member;
}

function findPrivateChat(chats, currentUser, member) {
    if (!currentUser?.id || !member?.id) return null;
    return (chats || []).find((chat) => (
        chat?.type === "private"
        && (chat.users || []).some((item) => String(item?.member?.id) === String(currentUser.id))
        && (chat.users || []).some((item) => String(item?.member?.id) === String(member.id))
    )) || null;
}

function findReferencedItem(items, reference) {
    return (items || []).find((item) => sameEntity(item, reference));
}

function workspaceDeliverables(projects) {
    return (projects || []).flatMap((project) => project?.deliverables || []);
}

function getChatTitle(chat, currentUser) {
    if (["group", "project"].includes(chat?.type)) return chat?.title || "گروه بدون عنوان";
    return getOtherUser(chat, currentUser)?.fullname || "گفتگوی خصوصی";
}

function ConversationAvatar({chat, member, currentUser, size = "large"}) {
    const privateMember = member || getOtherUser(chat, currentUser);
    if (chat?.type === "private" && privateMember) return <Avatar size={size} memberData={privateMember}/>;
    return <PrimeAvatar icon={chat?.type === "project" ? "pi pi-folder" : "pi pi-users"} size={size} shape="circle" className="bg-primary-100 text-primary"/>;
}

function EmptyConversation({privateMode, hasTargets}) {
    return (
        <div className="h-full flex flex-column align-items-center justify-content-center text-center text-500 p-5">
            <Icon icon={privateMode ? "mi mi:person" : "mi mi:groups"} className="text-4xl mb-3 text-300"/>
            <span className="font-medium text-700 text-xs">
                {hasTargets ? "یک گفتگو را انتخاب کنید" : privateMode ? "عضو دیگری در فضای کاری نیست" : "هنوز گروهی ساخته نشده است"}
            </span>
        </div>
    );
}

function DialogHeader({title, description}) {
    return (
        <div className="flex flex-column">
            <span className="text-lg">{title}</span>
            {description && <span className="text-xs text-400">{description}</span>}
        </div>
    );
}

function DialogFooter({submitLabel, submitIcon, loading, disabled, onSubmit, onCancel}) {
    return (
        <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
            <Button
                icon={<Icon icon={submitIcon} className="text-lg"/>}
                className="text-xs flex gap-1 border-round-lg p-2"
                label={submitLabel}
                loading={loading}
                disabled={disabled}
                onClick={onSubmit}
            />
            <Button
                className="text-xs p-button-secondary p-button-outlined border-round-lg p-2"
                label="انصراف"
                onClick={onCancel}
            />
        </div>
    );
}

function MemberMultiSelect({value, options, onChange, placeholder}) {
    const ItemTemplate = (member) => member ? (
        <div className="w-fit flex align-items-center gap-2 text-xs">
            <Avatar size="large" memberData={member}/>
            <span>{member.fullname}</span>
        </div>
    ) : (
        <div className="p-2">{placeholder}</div>
    );
    const SelectedItemTemplate = (member) => member ? (
        <div className="w-fit flex align-items-center gap-1 text-xs p-1 px-2 surface-100 border-round-2xl">
            <Avatar memberData={member}/>
            <span>{member.fullname}</span>
        </div>
    ) : null;

    return (
        <div className="border-round border-1 border-300 flex justify-content-between pr-2">
            <MultiSelect
                selectedItemTemplate={SelectedItemTemplate}
                filterTemplate={(filterOptions) => (
                    <InputText
                        placeholder="جستجوی اعضا"
                        onChange={filterOptions.filterOptions.filter}
                        className="p-2 w-full text-xs"
                    />
                )}
                filter
                filterBy="fullname"
                placeholder={placeholder}
                pt={{
                    root: () => ({className: "w-full border-none shadow-none"}),
                    label: () => ({className: "flex gap-1"}),
                    labelContainer: () => ({className: "align-items-center flex pr-2"}),
                    trigger: () => ({className: "p-3"}),
                    filterInput: () => ({className: "p-0"}),
                    header: () => ({className: "p-2 flex-row-reverse"}),
                    item: () => ({className: "flex-row-reverse justify-content-between p-2"}),
                }}
                panelClassName="rtl"
                onChange={(event) => onChange(event.value)}
                itemTemplate={ItemTemplate}
                dataKey="id"
                options={options}
                value={value}
            />
        </div>
    );
}

function ConversationsTab({targetChatDocumentId}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {user} = useSelector((state) => state.auth);
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const translate = useTranslate();
    const {
        chats,
        chatMessages,
        chatParticipants,
        tasks,
        projects,
        goals,
        teams,
        customers,
    } = useSelector((state) => state.workspaceGraph);
    const chatApi = useChat();
    const chatParticipantApi = useChatParticipant();
    const messageApi = useChatMessage();
    const deliverableApi = useDeliverable();
    const goalApi = useGoal();
    const projectApi = useProject();
    const taskApi = useTask();
    const teamApi = useTeam();
    const userAccessApi = useUserAccess();
    const [conversationTabIndex, setConversationTabIndex] = useState(0);
    const [selectedPrivateMemberId, setSelectedPrivateMemberId] = useState(null);
    const [selectedChatKey, setSelectedChatKey] = useState(null);
    const [listSearch, setListSearch] = useState("");
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [referenceDataLoading, setReferenceDataLoading] = useState(false);
    const [messagePage, setMessagePage] = useState(1);
    const [hasOlderMessages, setHasOlderMessages] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const [messageSearch, setMessageSearch] = useState("");
    const [messageSearchResults, setMessageSearchResults] = useState([]);
    const [messageSearchLoading, setMessageSearchLoading] = useState(false);
    const [messageSearchTotal, setMessageSearchTotal] = useState(0);
    const [messageKind, setMessageKind] = useState(null);
    const [messageDate, setMessageDate] = useState(null);
    const [actionMessage, setActionMessage] = useState(null);
    const [messageText, setMessageText] = useState("");
    const [messageMentions, setMessageMentions] = useState([]);
    const [linkedEntities, setLinkedEntities] = useState([]);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [uploadVisible, setUploadVisible] = useState(false);
    const [uploadChatKey, setUploadChatKey] = useState(null);
    const [createGroupVisible, setCreateGroupVisible] = useState(false);
    const [editGroupVisible, setEditGroupVisible] = useState(false);
    const [inviteVisible, setInviteVisible] = useState(false);
    const [dialogError, setDialogError] = useState("");
    const [groupTitle, setGroupTitle] = useState("");
    const [groupMembers, setGroupMembers] = useState([]);
    const [editGroupTitle, setEditGroupTitle] = useState("");
    const [editGroupDescription, setEditGroupDescription] = useState("");
    const [editGroupMembers, setEditGroupMembers] = useState([]);
    const [inviteMembers, setInviteMembers] = useState([]);
    const [selectedReferenceTask, setSelectedReferenceTask] = useState();
    const [selectedReferenceGoal, setSelectedReferenceGoal] = useState();
    const [selectedReferenceProject, setSelectedReferenceProject] = useState();
    const [selectedReferenceDeliverable, setSelectedReferenceDeliverable] = useState();
    const [createFromMessage, setCreateFromMessage] = useState(null);
    const [messageContext, setMessageContext] = useState(null);
    const [reactingMessageKey, setReactingMessageKey] = useState(null);
    const messageListRef = useRef(null);
    const shouldScrollToBottomRef = useRef(true);
    const messageContextMenuRef = useRef(null);
    const inboxToastRef = useRef(null);
    const pendingContextEventRef = useRef(null);
    const contextTitleRef = useRef("");
    const appliedTargetChatRef = useRef(null);
    const privateMode = conversationTabIndex === 0;

    const workspaceMemberships = useMemo(() => (
        workspaceMembershipData?.workspace?.workspace_memberships || []
    ), [workspaceMembershipData]);
    const mentionMembers = useMemo(() => (
        workspaceMemberships
            .map((membership) => membership?.member)
            .filter((member) => member?.id)
    ), [workspaceMemberships]);
    const membershipForMember = (member) => workspaceMemberships.find((membership) =>
        String(membership?.member?.id) === String(member?.id));
    const workspaceMembers = useMemo(() => (
        mentionMembers.filter((member) => String(member.id) !== String(user?.id))
    ), [mentionMembers, user?.id]);
    const referenceSuggestions = useMemo(() => buildReferenceSuggestions({
        tasks,
        projects,
        goals,
        teams,
        customers,
    }, translate), [customers, goals, projects, tasks, teams, translate]);

    useEffect(() => {
        const workspaceId = workspaceMembershipData?.workspace?.id;
        if (!workspaceId) return;

        let cancelled = false;
        setReferenceDataLoading(true);
        Promise.allSettled([
            taskApi.getTasks(null, workspaceId, {throwOnError: true}),
            projectApi.getProjects(workspaceId, false, {throwOnError: true}),
            goalApi.getGoals(workspaceId),
            teamApi.getTeams(workspaceId),
        ]).then(([taskResult, projectResult, goalResult, teamResult]) => {
            if (cancelled) return;
            if (taskResult.status === "fulfilled" && Array.isArray(taskResult.value)) {
                dispatch(mergeWorkspaceTasks(taskResult.value));
            }
            if (projectResult.status === "fulfilled" && Array.isArray(projectResult.value)) {
                dispatch(setWorkspaceGraphProjects(projectResult.value));
            }
            if (goalResult.status === "fulfilled" && Array.isArray(goalResult.value)) {
                dispatch(setWorkspaceGraphGoals(goalResult.value));
            }
            if (teamResult.status === "fulfilled" && Array.isArray(teamResult.value)) {
                dispatch(setWorkspaceGraphTeams(teamResult.value));
            }
        }).finally(() => {
            if (!cancelled) setReferenceDataLoading(false);
        });

        return () => {
            cancelled = true;
        };
        // API hook methods intentionally have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, workspaceMembershipData?.workspace?.id]);
    const ownParticipantFor = (chat) => (chatParticipants || []).find((participant) => (
        sameEntity(participant?.chat, chat)
        && String(entityKey(participant?.user)) === String(entityKey(workspaceMembershipData))
    ));

    const privateEntries = useMemo(() => {
        const needle = listSearch.trim().toLocaleLowerCase("fa");
        return workspaceMembers
            .map((member) => ({member, chat: findPrivateChat(chats, user, member)}))
            .filter(({member, chat}) => {
                if (!needle) return true;
                return [member.fullname, chat?.last_chat_message?.messageContent]
                    .some((value) => String(value || "").toLocaleLowerCase("fa").includes(needle));
            })
            .sort((left, right) => {
                const leftDate = new Date(left.chat?.lastMessageAt || left.chat?.updatedAt || 0).getTime();
                const rightDate = new Date(right.chat?.lastMessageAt || right.chat?.updatedAt || 0).getTime();
                if (leftDate !== rightDate) return rightDate - leftDate;
                return String(left.member.fullname || "").localeCompare(String(right.member.fullname || ""), "fa");
            });
    }, [chats, listSearch, user, workspaceMembers]);

    const groupChats = useMemo(() => {
        const needle = listSearch.trim().toLocaleLowerCase("fa");
        return [...(chats || [])]
            .filter((chat) => ["group", "project"].includes(chat?.type) && !ownParticipantFor(chat)?.isArchived)
            .filter((chat) => {
                if (!needle) return true;
                return [chat.title, chat.description, chat?.last_chat_message?.messageContent]
                    .some((value) => String(value || "").toLocaleLowerCase("fa").includes(needle));
            })
            .sort((left, right) => (
                new Date(right?.lastMessageAt || right?.updatedAt || 0)
                - new Date(left?.lastMessageAt || left?.updatedAt || 0)
            ));
        // ownParticipantFor is derived from the dependencies below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chats, chatParticipants, listSearch, user?.id]);
    const unreadMessagesByType = useMemo(() => (
        (chats || []).reduce((counts, chat) => {
            if (!["private", "group", "project"].includes(chat?.type)) return counts;

            const participant = (chatParticipants || []).find((item) => (
                sameEntity(item?.chat, chat)
                && String(entityKey(item?.user)) === String(entityKey(workspaceMembershipData))
            ));
            if (participant?.isArchived) return counts;

            counts[chat.type === "project" ? "group" : chat.type] += Math.max(0, Number(participant?.unreadCount || 0));
            return counts;
        }, {private: 0, group: 0})
    ), [chatParticipants, chats, workspaceMembershipData]);

    const selectedPrivateMember = useMemo(() => (
        workspaceMembers.find((member) => String(member.id) === String(selectedPrivateMemberId)) || null
    ), [selectedPrivateMemberId, workspaceMembers]);
    const selectedChat = useMemo(() => {
        if (privateMode) return findPrivateChat(chats, user, selectedPrivateMember);
        return (chats || []).find((chat) => String(entityKey(chat)) === String(selectedChatKey)) || null;
    }, [chats, privateMode, selectedChatKey, selectedPrivateMember, user]);
    const selectedChatDocumentKey = entityKey(selectedChat);
    const selectedTarget = privateMode ? selectedPrivateMember : selectedChat;

    useEffect(() => {
        if (!targetChatDocumentId || appliedTargetChatRef.current === targetChatDocumentId) return;
        const targetChat = (chats || []).find((chat) => (
            String(entityKey(chat)) === String(targetChatDocumentId)
        ));
        if (!targetChat) return;

        if (["group", "project"].includes(targetChat.type)) {
            setConversationTabIndex(1);
            setSelectedChatKey(entityKey(targetChat));
        } else {
            const otherUser = (targetChat.users || []).find((chatUser) => (
                String(chatUser?.member?.id) !== String(user?.id)
            ))?.member;
            if (!otherUser?.id) return;
            setConversationTabIndex(0);
            setSelectedPrivateMemberId(otherUser.id);
        }

        appliedTargetChatRef.current = targetChatDocumentId;
    }, [chats, targetChatDocumentId, user?.id]);

    useEffect(() => {
        const contextEvent = pendingContextEventRef.current;
        if (!messageContext || !contextEvent) return;

        pendingContextEventRef.current = null;
        messageContextMenuRef.current?.show(contextEvent);
    }, [messageContext]);

    useEffect(() => {
        if (!privateMode) return;
        const selectedExists = privateEntries.some(({member}) => String(member.id) === String(selectedPrivateMemberId));
        if (!selectedExists) setSelectedPrivateMemberId(privateEntries[0]?.member?.id || null);
    }, [privateEntries, privateMode, selectedPrivateMemberId]);

    useEffect(() => {
        if (privateMode) return;
        const selectedExists = groupChats.some((chat) => String(entityKey(chat)) === String(selectedChatKey));
        if (!selectedExists) setSelectedChatKey(entityKey(groupChats[0]));
    }, [groupChats, privateMode, selectedChatKey]);

    useEffect(() => {
        setActionMessage(null);
        setMessageText("");
        setMessageMentions([]);
        setLinkedEntities([]);
        setPendingAttachments([]);
        setMessageSearch("");
        setMessageSearchResults([]);
        setMessageSearchTotal(0);
        setMessagePage(1);
        setHasOlderMessages(false);
        shouldScrollToBottomRef.current = true;
        if (!selectedChat) {
            setMessagesLoading(false);
            return;
        }

        let cancelled = false;
        setMessagesLoading(true);
        Promise.all([
            messageApi.getChatMessagePage(selectedChat, {page: 1, pageSize: 40, replace: true}),
            chatApi.markChatRead(entityKey(selectedChat)),
        ]).then(([result]) => {
            if (cancelled) return;
            const pagination = result?.pagination;
            setMessagePage(Number(pagination?.page || 1));
            setHasOlderMessages(Number(pagination?.page || 1) < Number(pagination?.pageCount || 1));
        }).catch((error) => {
            if (!cancelled) console.error("[Inbox conversations]", error);
        }).finally(() => {
            if (!cancelled) setMessagesLoading(false);
        });
        return () => {
            cancelled = true;
        };
        // API hook methods intentionally have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChatDocumentKey]);

    const messages = useMemo(() => sortMessagesChronologically(
        (chatMessages || []).filter((item) => sameEntity(item?.chat, selectedChat))
    ), [chatMessages, selectedChat]);
    const latestSeenMessageIndex = useMemo(() => (
        messages.reduce((latestIndex, message, index) => (
            message?.seenAt ? index : latestIndex
        ), -1)
    ), [messages]);
    const seenMessageKeys = useMemo(() => new Set(
        messages.slice(0, latestSeenMessageIndex + 1).map((message) => String(entityKey(message)))
    ), [latestSeenMessageIndex, messages]);
    const normalizedMessageSearch = messageSearch.trim();
    const messageDateRange = useMemo(() => messageDateRangeFor(messageDate), [messageDate]);
    const messageFiltersActive = Boolean(normalizedMessageSearch || messageKind || messageDateRange);
    const visibleMessages = useMemo(() => (
        messageFiltersActive
            ? sortMessagesChronologically(messageSearchResults)
            : messages
    ), [messageSearchResults, messages, messageFiltersActive]);
    const messageGroups = useMemo(() => groupMessagesByPersianDay(visibleMessages), [visibleMessages]);
    const selectedOwnParticipant = ownParticipantFor(selectedChat);
    const availableMentionMembers = useMemo(() => {
        const chatUsers = selectedChat?.users?.length
            ? selectedChat.users
            : privateMode && selectedPrivateMember
                ? [user, selectedPrivateMember]
                : [];
        const allowedIds = new Set(chatUsers
            .map((membership) => membership?.member || membership)
            .map((member) => String(member?.id))
            .filter(Boolean));
        return mentionMembers.filter((member) => allowedIds.has(String(member.id)));
    }, [mentionMembers, privateMode, selectedChat, selectedPrivateMember, user]);

    useEffect(() => {
        if (!selectedChat || !selectedOwnParticipant?.unreadCount) return;
        chatApi.markChatRead(entityKey(selectedChat)).catch((error) => {
            console.error("[Inbox mark active chat read]", error);
        });
        // API hook methods intentionally have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChatDocumentKey, selectedOwnParticipant?.unreadCount]);

    useEffect(() => {
        setMessageKind(null);
        setMessageDate(null);
    }, [selectedChatDocumentKey, selectedPrivateMemberId]);

    useEffect(() => {
        if (!selectedChat || !messageFiltersActive) {
            setMessageSearchResults([]);
            setMessageSearchLoading(false);
            setMessageSearchTotal(0);
            return;
        }

        let cancelled = false;
        const timeoutId = window.setTimeout(() => {
            setMessageSearchLoading(true);
            messageApi.getChatMessagePage(selectedChat, {
                page: 1,
                pageSize: 100,
                search: normalizedMessageSearch,
                kind: messageKind,
                since: messageDateRange?.since,
                until: messageDateRange?.until,
            }).then((result) => {
                if (cancelled) return;
                setMessageSearchResults(result?.items || []);
                setMessageSearchTotal(Number(result?.pagination?.total || result?.items?.length || 0));
            }).catch((error) => {
                if (!cancelled) console.error("[Inbox message search]", error);
            }).finally(() => {
                if (!cancelled) setMessageSearchLoading(false);
            });
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
        // API hook methods intentionally have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedMessageSearch, messageKind, messageDateRange, selectedChatDocumentKey]);

    useEffect(() => {
        if (!messageListRef.current || loadingOlderMessages || messageFiltersActive) return;
        if (!shouldScrollToBottomRef.current) return;
        window.requestAnimationFrame(() => {
            if (messageListRef.current) {
                messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
            }
        });
    }, [loadingOlderMessages, messages.length, messageFiltersActive, selectedChatDocumentKey]);

    const loadOlderMessages = async () => {
        if (!selectedChat || !hasOlderMessages || loadingOlderMessages || messageFiltersActive) return;
        const list = messageListRef.current;
        const previousScrollHeight = list?.scrollHeight || 0;
        const nextPage = messagePage + 1;
        setLoadingOlderMessages(true);
        shouldScrollToBottomRef.current = false;
        try {
            const result = await messageApi.getChatMessagePage(selectedChat, {
                page: nextPage,
                pageSize: 40,
            });
            const pagination = result?.pagination;
            setMessagePage(Number(pagination?.page || nextPage));
            setHasOlderMessages(Number(pagination?.page || nextPage) < Number(pagination?.pageCount || nextPage));
            window.requestAnimationFrame(() => {
                if (!messageListRef.current) return;
                messageListRef.current.scrollTop = messageListRef.current.scrollHeight - previousScrollHeight;
            });
        } catch (error) {
            console.error("[Inbox older messages]", error);
        } finally {
            setLoadingOlderMessages(false);
        }
    };

    const handleMessageListScroll = (event) => {
        const list = event.currentTarget;
        shouldScrollToBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
        if (list.scrollTop <= 48) loadOlderMessages();
    };

    const setChatStatus = async (status, chat = selectedChat) => {
        if (!chat || !user?.id) return;
        const participant = ownParticipantFor(chat);
        if (!participant) return;
        try {
            await chatParticipantApi.updateChatParticipant(entityKey(participant), {
                participantState: status || "active",
            }, {quiet: true});
        } catch (error) {
            console.error("[Inbox chat status]", error);
        }
    };

    const ensureSelectedChat = async () => {
        if (selectedChat) return selectedChat;
        if (!privateMode || !selectedPrivateMember?.id) return null;

        const targetMembership = membershipForMember(selectedPrivateMember);
        if (!targetMembership) return null;
        const chat = await chatApi.createChat({
            type: "private",
            users: {connect: [{documentId: targetMembership.documentId}]},
        });
        if (chat) setSelectedChatKey(entityKey(chat));
        return chat;
    };

    const openAttachmentUpload = async () => {
        try {
            const chat = await ensureSelectedChat();
            if (!chat) return;
            setUploadChatKey(entityKey(chat));
            setUploadVisible(true);
            await setChatStatus("sending-file", chat);
        } catch (error) {
            console.error("[Inbox chat attachment]", error);
        }
    };

    const sendMessage = async (data = {}, parent = null) => {
        const content = data.messageContent !== undefined ? data.messageContent : messageText;
        const mentions = data.mentions !== undefined ? valueList(data.mentions) : messageMentions;
        const references = data.linkedEntity !== undefined ? valueList(data.linkedEntity) : linkedEntities;
        const attachments = data.attachment !== undefined ? valueList(data.attachment) : pendingAttachments;
        if (!selectedTarget || (!String(content || "").trim() && !attachments.length && !references.length)) return null;

        setSending(true);
        try {
            const chat = await ensureSelectedChat();
            if (!chat) return null;
            const item = await messageApi.createChatMessage({
                ...data,
                chat: entityKey(chat),
                messageContent: String(content || "").trim() || null,
                attachment: attachments.length
                    ? chatMessageAttachmentIdentifiers(attachments)
                    : undefined,
                mentions: mentions.map((mention) => ({id: mention.id, token: mention.token})),
                linkedEntity: references.map((reference) => ({
                    type: reference.type,
                    documentId: reference.documentId,
                    id: reference.id,
                    token: reference.token,
                })),
                parent: parent ? entityKey(parent) : actionMessage?.action === "reply" ? entityKey(actionMessage.message) : null,
                clientNonce: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            });
            setMessageText("");
            setMessageMentions([]);
            setLinkedEntities([]);
            setPendingAttachments([]);
            setActionMessage(null);
            await setChatStatus("", chat);
            return item;
        } finally {
            setSending(false);
        }
    };

    const submitComposer = async () => {
        if (actionMessage?.action === "edit") {
            if (!messageText.trim() && !linkedEntities.length) return;
            setSending(true);
            try {
                await messageApi.updateChatMessage(entityKey(actionMessage.message), {
                    messageContent: messageText.trim(),
                    mentions: messageMentions.map((mention) => ({id: mention.id, token: mention.token})),
                    linkedEntity: linkedEntities.map((reference) => ({
                        type: reference.type,
                        documentId: reference.documentId,
                        id: reference.id,
                        token: reference.token,
                    })),
                });
                setActionMessage(null);
                setMessageText("");
                setMessageMentions([]);
                setLinkedEntities([]);
                setPendingAttachments([]);
            } finally {
                setSending(false);
            }
            return;
        }
        await sendMessage();
    };

    const chooseMessageAction = (action, message) => {
        setActionMessage({action, message});
        setMessageText(action === "edit" ? message?.messageContent || "" : "");
        setMessageMentions(action === "edit" ? valueList(message?.mentions) : []);
        setLinkedEntities(action === "edit" ? valueList(message?.linkedEntity) : []);
        setPendingAttachments([]);
    };

    const removeMessage = (message) => {
        confirmDialog({
            group: "delete-chat-message",
            header: "حذف پیام",
            message: "این پیام برای همیشه حذف شود؟",
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "reject",
            acceptLabel: "حذف",
            rejectLabel: "لغو",
            accept: async () => {
                await messageApi.deleteChatMessage(message);
                if (sameEntity(actionMessage?.message, message)) {
                    setActionMessage(null);
                    setMessageText("");
                    setMessageMentions([]);
                    setLinkedEntities([]);
                    setPendingAttachments([]);
                }
            },
        });
    };

    const handleComposerChange = (event) => {
        const value = event.target.value;
        setMessageText(value);
        setMessageMentions((current) => current.filter((mention) => (
            value.includes(`@${mention.token || mentionToken(mention)}`)
        )));
        setLinkedEntities((current) => current.filter((reference) => (
            value.includes(`/${reference.token || referenceToken(reference.type, reference.title)}`)
        )));
    };

    const handleComposerSelect = ({suggestion}) => {
        if (suggestion.suggestionType === "mention") {
            const mention = {
                id: suggestion.id,
                documentId: suggestion.documentId,
                fullname: suggestion.fullname,
                username: suggestion.username,
                token: suggestion.insertText,
            };
            setMessageMentions((current) => current.some((item) => String(item.id) === String(mention.id))
                ? current
                : [...current, mention]);
            return;
        }

        const reference = {
            type: suggestion.type,
            id: suggestion.id,
            documentId: suggestion.documentId,
            title: suggestion.title,
            token: suggestion.insertText,
        };
        setLinkedEntities((current) => current.some((item) => (
            item.type === reference.type && String(entityKey(item)) === String(entityKey(reference))
        )) ? current : [...current, reference]);
    };

    const createGroup = async () => {
        setDialogError("");
        if (!groupTitle.trim()) {
            setDialogError("نام گروه را وارد کنید.");
            return;
        }
        if (!groupMembers.length) {
            setDialogError("حداقل یک عضو را برای گروه انتخاب کنید.");
            return;
        }

        try {
            const chat = await chatApi.createChat({
                type: "group",
                title: groupTitle.trim(),
                users: {connect: groupMembers
                    .map(membershipForMember)
                    .filter(Boolean)
                    .map((membership) => ({documentId: membership.documentId}))},
            });
            if (chat) setSelectedChatKey(entityKey(chat));
            setConversationTabIndex(1);
            setCreateGroupVisible(false);
            setGroupTitle("");
            setGroupMembers([]);
        } catch (error) {
            setDialogError(error?.message || "ایجاد گروه ناموفق بود.");
        }
    };

    const openEditGroup = () => {
        if (!selectedChat || selectedChat.type !== "group") return;
        const selectedIds = new Set((selectedChat.users || []).map((member) => String(member?.member?.id)));
        setDialogError("");
        setEditGroupTitle(selectedChat.title || "");
        setEditGroupDescription(selectedChat.description || "");
        setEditGroupMembers(workspaceMembers.filter((member) => selectedIds.has(String(member.id))));
        setEditGroupVisible(true);
    };

    const updateGroup = async () => {
        if (!selectedChat || selectedChat.type !== "group") return;
        setDialogError("");
        if (!editGroupTitle.trim()) {
            setDialogError("نام گروه را وارد کنید.");
            return;
        }
        if (!editGroupMembers.length) {
            setDialogError("حداقل یک عضو باید در گروه باقی بماند.");
            return;
        }

        try {
            await chatApi.updateChat(entityKey(selectedChat), {
                title: editGroupTitle.trim(),
                description: editGroupDescription.trim() || null,
                users: {set: editGroupMembers
                    .map(membershipForMember)
                    .filter(Boolean)
                    .map((membership) => ({documentId: membership.documentId}))},
            });
            setEditGroupVisible(false);
        } catch (error) {
            setDialogError(error?.message || "ویرایش گروه ناموفق بود.");
        }
    };

    const removeGroup = () => {
        const group = selectedChat;
        if (!group || group.type !== "group") return;

        confirmDialog({
            group: "delete-chat-group",
            header: "حذف گروه",
            message: `گروه «${getChatTitle(group, user)}» برای همیشه حذف شود؟`,
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "reject",
            acceptLabel: "حذف",
            rejectLabel: "لغو",
            accept: async () => {
                await chatApi.deleteChat(group);
                setSelectedChatKey(null);
                setEditGroupVisible(false);
                setInviteVisible(false);
            },
        });
    };

    const submitMemberInvites = async () => {
        if (!selectedChat || !inviteMembers.length) return;
        const existingIds = (selectedChat.users || [])
            .filter((member) => String(member?.member?.id) !== String(user?.id))
            .map((member) => member?.member?.id)
            .filter(Boolean);
        const allMemberIds = [...new Set([...existingIds, ...inviteMembers.map((member) => member.id)])];
        const users = allMemberIds
            .map((memberId) => membershipForMember({id: memberId}))
            .filter(Boolean);

        try {
            await chatApi.updateChat(entityKey(selectedChat), {
                users: {connect: users.map((membership) => ({
                    documentId: membership.documentId,
                }))},
            });
            setInviteVisible(false);
            setInviteMembers([]);
        } catch (error) {
            setDialogError(error?.message || "دعوت اعضا ناموفق بود.");
        }
    };

    const refreshWorkspaceProjects = async () => {
        const workspaceId = workspaceMembershipData?.workspace?.id;
        if (!workspaceId) return [];

        const refreshedProjects = await projectApi.getProjects(workspaceId);
        if (!Array.isArray(refreshedProjects)) return [];

        dispatch(setWorkspaceGraphProjects(refreshedProjects));
        dispatch(setWorkspaceMembership({
            ...workspaceMembershipData,
            workspace: {
                ...workspaceMembershipData.workspace,
                projects: refreshedProjects,
            },
        }));
        return refreshedProjects;
    };

    const closeReferenceSidebars = () => {
        setSelectedReferenceTask(undefined);
        setSelectedReferenceGoal(undefined);
        setSelectedReferenceProject(undefined);
        setSelectedReferenceDeliverable(undefined);
    };

    const handleOpenReference = async (reference) => {
        const type = reference?.type === "milestone" ? "deliverable" : reference?.type;
        const referenceKey = entityKey(reference);
        closeReferenceSidebars();

        if (type === "task") {
            const task = findReferencedItem(tasks, reference)
                || await taskApi.getTask(referenceKey);
            if (task) {
                dispatch(upsertWorkspaceTask(task));
                setSelectedReferenceTask(task);
                return;
            }
        } else if (type === "goal") {
            const goal = findReferencedItem(goals, reference)
                || await goalApi.getGoal(referenceKey);
            if (goal) {
                dispatch(upsertWorkspaceGoal(goal));
                setSelectedReferenceGoal(goal);
                return;
            }
        } else if (type === "project") {
            const project = findReferencedItem(projects, reference)
                || await projectApi.getProject(referenceKey);
            if (project) {
                dispatch(upsertWorkspaceProject(project));
                setSelectedReferenceProject(project);
                return;
            }
        } else if (type === "deliverable") {
            const deliverable = findReferencedItem(workspaceDeliverables(projects), reference)
                || await deliverableApi.getDeliverable(referenceKey);
            if (deliverable) {
                setSelectedReferenceDeliverable(deliverable);
                return;
            }
        }

        if (reference?.link) navigate(reference.link);
    };

    const updateReferenceTask = async (taskData, updateData, subTasks = []) => {
        let updatedTask = null;

        if (updateData) {
            updatedTask = await taskApi.updateTask(entityKey(taskData), updateData);
            if (updatedTask) {
                dispatch(upsertWorkspaceTask(updatedTask));
                setSelectedReferenceTask((current) => sameEntity(current, taskData) ? updatedTask : current);
            }
        }

        await Promise.all((subTasks || []).map(async (subTask) => {
            const subTaskData = {...(subTask || {})};
            const documentId = subTaskData.documentId;
            delete subTaskData.id;
            delete subTaskData.documentId;
            delete subTaskData.createdAt;
            delete subTaskData.updatedAt;
            const dueDate = subTaskData.dueDate
                ? Number.isInteger(subTaskData.dueDate)
                    ? new Date(subTaskData.dueDate * 1000).toISOString()
                    : subTaskData.dueDate
                : Date.now();
            const item = documentId
                ? await taskApi.updateTask(documentId, {...subTaskData, dueDate})
                : await taskApi.createTask({
                    ...subTaskData,
                    parentTask: taskData?.id,
                    dueDate,
                    workspace: workspaceMembershipData?.workspace?.id,
                });
            if (item) dispatch(upsertWorkspaceTask(item));
            return item;
        }));

        return updatedTask;
    };

    const updateReferenceProject = async (projectData, updateData) => {
        const updatedProject = await projectApi.updateProject(entityKey(projectData), updateData);
        if (updatedProject) {
            dispatch(upsertWorkspaceProject(updatedProject));
            setSelectedReferenceProject((current) => sameEntity(current, projectData) ? updatedProject : current);
        }
        return updatedProject;
    };

    const updateReferenceDeliverable = async (updateData) => {
        if (!selectedReferenceDeliverable) return null;
        const updatedDeliverable = await deliverableApi.updateDeliverable(
            entityKey(selectedReferenceDeliverable),
            updateData
        );
        if (!updatedDeliverable) return null;

        setSelectedReferenceDeliverable(updatedDeliverable);
        const refreshedProjects = await refreshWorkspaceProjects();
        const refreshedDeliverable = findReferencedItem(
            workspaceDeliverables(refreshedProjects),
            updatedDeliverable
        );
        if (refreshedDeliverable) setSelectedReferenceDeliverable(refreshedDeliverable);
        return updatedDeliverable;
    };

    const handleMessageTextContextMenu = (event, message, selectedText) => {
        const title = String(selectedText || message?.messageContent || "")
            .replace(/\s+/g, " ")
            .trim();
        event.preventDefault();
        event.stopPropagation();
        contextTitleRef.current = title;
        pendingContextEventRef.current = event.nativeEvent || event;
        setMessageContext({message, text: title});
    };

    const handleMessageReaction = async (message, emoji) => {
        const messageKey = entityKey(message);
        if (!messageKey || reactingMessageKey === messageKey) return false;

        setReactingMessageKey(messageKey);
        try {
            await messageApi.setChatMessageReaction(message, emoji);
            return true;
        } catch {
            inboxToastRef.current?.show({
                severity: "error",
                summary: translate("inbox.message.reaction.error.summary"),
                detail: translate("inbox.message.reaction.error.detail"),
                life: 5000,
            });
            return false;
        } finally {
            setReactingMessageKey(null);
        }
    };

    const handleAttachmentError = () => {
        inboxToastRef.current?.show({
            severity: "error",
            summary: translate("inbox.attachment.error.summary"),
            detail: translate("inbox.attachment.error.detail"),
            life: 5000,
        });
    };

    const openCreateFromMessage = (type) => {
        const title = contextTitleRef.current.trim();
        if (title) setCreateFromMessage({type, title});
    };

    const createTaskFromMessage = async (taskData, subTasks = []) => {
        const workspaceId = workspaceMembershipData?.workspace?.id;
        if (!taskData || !workspaceId) return;

        const parentTask = await taskApi.createTask({
            ...taskData,
            dueDate: Date.now(),
            workspace: workspaceId,
        });
        if (!parentTask) return;
        dispatch(upsertWorkspaceTask(parentTask));

        await Promise.all((subTasks || []).map(async (subTask) => {
            const subTaskData = {...(subTask || {})};
            delete subTaskData.id;
            delete subTaskData.documentId;
            const createdSubTask = await taskApi.createTask({
                ...subTaskData,
                parentTask: parentTask.id,
                dueDate: Date.now(),
                workspace: workspaceId,
            });
            if (createdSubTask) dispatch(upsertWorkspaceTask(createdSubTask));
            return createdSubTask;
        }));

        setCreateFromMessage(null);
    };

    const createDeliverableFromMessage = async (deliverableData) => {
        const workspaceId = workspaceMembershipData?.workspace?.id;
        if (!deliverableData || !workspaceId) return;

        const createdDeliverable = await deliverableApi.createDeliverable({
            ...deliverableData,
            workspace: workspaceId,
        });
        if (!createdDeliverable) return;

        await refreshWorkspaceProjects();
        setCreateFromMessage(null);
    };

    const createProjectFromMessage = async ({value, payload}) => {
        const workspaceId = workspaceMembershipData?.workspace?.id;
        if (!value || !workspaceId) return;

        const createdProject = await projectApi.createProject({...value, workspace: workspaceId});
        if (!createdProject) return;
        dispatch(upsertWorkspaceProject(createdProject));

        const memberRoles = (payload?.members || []).map((member) => ({
            workspace_membership: member.id,
            role: payload?.status ? "viewer" : member.role,
        }));
        if (memberRoles.length) {
            await Promise.all(memberRoles.map((roleData) => userAccessApi.createUserAccess({
                ...roleData,
                scope_type: "project",
                scope_id: createdProject.documentId,
                workspace: workspaceId,
            })));
        }

        await refreshWorkspaceProjects();
        setCreateFromMessage(null);
    };

    const inviteOptions = workspaceMembers.filter((option) => !selectedChat?.users?.some((member) => (
        String(member?.member?.id) === String(option.id)
    )));
    const canManageGroup = selectedChat?.type === "group"
        && String(entityKey(selectedChat?.createdByUser)) === String(entityKey(workspaceMembershipData));
    const activeStatuses = (chatParticipants || []).filter((item) => (
        sameEntity(item?.chat, selectedChat)
        && ["typing", "sending-file"].includes(item?.participantState)
        && String(entityKey(item?.user)) !== String(entityKey(workspaceMembershipData))
    ));

    const createGroupFooter = (
        <DialogFooter
            submitLabel="ایجاد گروه"
            submitIcon="mi mi:add"
            loading={chatApi.loading}
            disabled={!groupTitle.trim() || !groupMembers.length}
            onSubmit={createGroup}
            onCancel={() => setCreateGroupVisible(false)}
        />
    );
    const inviteFooter = (
        <DialogFooter
            submitLabel="دعوت اعضا"
            submitIcon="mi mi:person_add"
            loading={chatApi.loading}
            disabled={!inviteMembers.length}
            onSubmit={submitMemberInvites}
            onCancel={() => setInviteVisible(false)}
        />
    );
    const editGroupFooter = (
        <DialogFooter
            submitLabel="ذخیره تغییرات"
            submitIcon="mi mi:save"
            loading={chatApi.loading}
            disabled={!editGroupTitle.trim() || !editGroupMembers.length}
            onSubmit={updateGroup}
            onCancel={() => setEditGroupVisible(false)}
        />
    );
    const contextMessage = messageContext?.message;
    const contextOwnMessage = String(entityKey(contextMessage?.user)) === String(entityKey(workspaceMembershipData));
    const contextReactions = valueList(contextMessage?.reactions);
    const contextHasText = Boolean(messageContext?.text);
    const messageContextMenuItems = [
        {
            template: () => (
                <div
                    className="ltr flex align-items-center justify-content-center gap-1 p-1"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    {QUICK_REACTIONS.map((emoji) => (
                        <Button
                            key={emoji}
                            type="button"
                            text
                            className="w-2rem h-2rem p-1 flex align-items-center justify-content-center"
                            disabled={reactingMessageKey === entityKey(contextMessage)}
                            aria-label={`${translate("inbox.message.reaction.add")} ${emoji}`}
                            label={(
                                <TwemojiText imageClassName="inline-block vertical-align-middle w-1rem h-1rem">
                                    {emoji}
                                </TwemojiText>
                            )}
                            onClick={async () => {
                                const nextEmoji = nextReactionEmoji(
                                    contextMessage?.reactions,
                                    workspaceMembershipData,
                                    emoji
                                );
                                const saved = await handleMessageReaction(contextMessage, nextEmoji);
                                if (saved) messageContextMenuRef.current?.hide();
                            }}
                        />
                    ))}
                </div>
            ),
        },
        {
            label: translate("inbox.message.reaction.reacted-by"),
            icon: "mi mi:group",
            disabled: !contextReactions.length,
            items: contextReactions.map((reaction) => ({
                label: `${reaction.emoji} ${reactionActorName(
                    reaction,
                    translate("inbox.message.reaction.unknown-user")
                )}`,
            })),
        },
        {separator: true},
        {
            label: "پاسخ",
            icon: "mi mi:reply",
            command: () => {
                if (contextMessage) chooseMessageAction("reply", contextMessage);
            },
        },
        {separator: true},
        {
            label: "رونوشت",
            icon: "mi mi:content_copy",
            disabled: !contextHasText,
            command: () => copyToClipboard(messageContext?.text || ""),
        },
        {separator: true},
        {
            label: "ایجاد",
            icon: "mi mi:add",
            disabled: !contextHasText,
            items: [
                {
                    label: "تسک",
                    icon: "mi mi:add_task",
                    command: () => openCreateFromMessage("task"),
                },
                {
                    label: "مایلستون",
                    icon: "mi mi:flag",
                    command: () => openCreateFromMessage("deliverable"),
                },
                {
                    label: "پروژه",
                    icon: "mi mi:folder_open",
                    command: () => openCreateFromMessage("project"),
                },
            ],
        },
        {separator: true},
        {
            label: "حذف",
            icon: "mi mi:delete",
            disabled: !contextOwnMessage,
            command: () => {
                if (contextOwnMessage && contextMessage) removeMessage(contextMessage);
            },
        },
    ];

    return (
        <div className="h-full min-h-0 flex flex-column md:flex-row surface-card">
            <Toast ref={inboxToastRef} position="bottom-left"/>
            <aside className="w-full md:w-20rem flex-shrink-0 flex flex-column min-h-0 border-left-1 border-200">
                <div className="p-3 border-bottom-1 border-200">
                    <IconField className="align-items-center flex w-full">
                        <InputIcon className="pi pi-search"/>
                        <InputText
                            value={listSearch}
                            onChange={(event) => setListSearch(event.target.value)}
                            placeholder={privateMode ? "جستجوی اعضای فضای کاری" : "جستجوی گروه‌ها"}
                            className="w-full border-round-lg pt-2 pb-2 text-xs"
                        />
                    </IconField>
                </div>

                <div className="flex-1 min-h-0">
                    <TabView
                        lazy
                        activeIndex={conversationTabIndex}
                        onTabChange={(event) => {
                            setConversationTabIndex(event.index);
                            setListSearch("");
                            setActionMessage(null);
                            setMessageText("");
                            setMessageMentions([]);
                            setLinkedEntities([]);
                            setPendingAttachments([]);
                        }}
                        panelContainerClassName="flex-1 min-h-0 p-0 border-noround"
                        className="h-full flex flex-column"
                    >
                        <TabPanel
                            header={(
                                <span className="flex align-items-center gap-1">
                                    <span>خصوصی</span>
                                    {unreadMessagesByType.private > 0 && (
                                        <Badge value={unreadMessagesByType.private} severity="danger" className="conversation-tab-badge"/>
                                    )}
                                </span>
                            )}
                            leftIcon={<Icon icon="mi mi:person" className="pl-2"/>}
                            className="h-full text-xs"
                        >
                            {conversationTabIndex === 0 && (
                                <div className="h-full min-h-0 overflow-auto p-2">
                                    {!privateEntries.length && <div className="text-center text-500 text-xs p-4">عضوی پیدا نشد.</div>}
                                    {privateEntries.map(({member, chat}) => {
                                        const selected = String(member.id) === String(selectedPrivateMemberId);
                                        const participant = ownParticipantFor(chat);
                                        const preview = chat?.last_chat_message?.messageContent
                                            || chat?.last_chat_message?.attachment?.[0]?.name
                                            || "برای شروع پیام بفرستید";
                                        return (
                                            <button
                                                type="button"
                                                key={member.id}
                                                onClick={() => setSelectedPrivateMemberId(member.id)}
                                                className={`w-full border-none border-round p-2 mb-1 text-right cursor-pointer shabnam-font ${selected ? "surface-100" : "surface-card hover:surface-50"}`}
                                            >
                                                <div className="flex align-items-center gap-2">
                                                    <Avatar size="large" memberData={member}/>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex align-items-center justify-content-between gap-2">
                                                            <span className="font-medium text-xs overflow-hidden text-overflow-ellipsis white-space-nowrap">{member.fullname}</span>
                                                            {chat?.lastMessageAt && <Span type="TIME_SINCE" spanClassName="text-xs text-500 white-space-nowrap">{chat.lastMessageAt}</Span>}
                                                        </div>
                                                        <div className="flex align-items-center gap-2 mt-1">
                                                            <span className="flex-1 text-xs text-500 overflow-hidden text-overflow-ellipsis white-space-nowrap">{preview}</span>
                                                            {participant?.unreadCount > 0 && <Badge value={participant.unreadCount} severity="danger"/>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </TabPanel>

                        <TabPanel
                            header={(
                                <span className="flex align-items-center gap-1">
                                    <span>گروهی</span>
                                    {unreadMessagesByType.group > 0 && (
                                        <Badge value={unreadMessagesByType.group} severity="danger" className="conversation-tab-badge"/>
                                    )}
                                </span>
                            )}
                            leftIcon={<Icon icon="mi mi:groups" className="pl-2"/>}
                            className="h-full text-xs"
                        >
                            {conversationTabIndex === 1 && (
                                <div className="h-full min-h-0 flex flex-column">
                                    <div className="p-2 border-bottom-1 border-100">
                                        <Button
                                            label="ایجاد گروه"
                                            icon={<Icon icon="mi mi:add" className="text-sm"/>}
                                            className="w-full text-xs gap-1 p-2"
                                            onClick={() => { setDialogError(""); setCreateGroupVisible(true); }}
                                        />
                                    </div>
                                    <div className="flex-1 min-h-0 overflow-auto p-2">
                                        {!groupChats.length && <div className="text-center text-500 text-xs p-4">گروهی پیدا نشد.</div>}
                                        {groupChats.map((chat) => {
                                            const selected = sameEntity(chat, selectedChat);
                                            const participant = ownParticipantFor(chat);
                                            const preview = chat?.last_chat_message?.messageContent
                                                || chat?.last_chat_message?.attachment?.[0]?.name
                                                || "هنوز پیامی ارسال نشده";
                                            return (
                                                <button
                                                    type="button"
                                                    key={entityKey(chat)}
                                                    onClick={() => setSelectedChatKey(entityKey(chat))}
                                                    className={`w-full border-none border-round p-2 mb-1 text-right cursor-pointer shabnam-font ${selected ? "surface-100" : "surface-card hover:surface-50"}`}
                                                >
                                                    <div className="flex align-items-center gap-2">
                                                        <ConversationAvatar chat={chat} currentUser={user}/>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex align-items-center justify-content-between gap-2">
                                                                <span className="flex align-items-center gap-1 min-w-0">
                                                                    <span className="font-medium text-xs overflow-hidden text-overflow-ellipsis white-space-nowrap">{getChatTitle(chat, user)}</span>
                                                                    <span className="text-xs text-500 white-space-nowrap flex align-items-center gap-1">
                                                                        <Icon icon="mi mi:group" className="text-xs"/>
                                                                        {Number(chat?.users?.length || 0).toLocaleString("fa-IR")}
                                                                    </span>
                                                                </span>
                                                                {chat?.lastMessageAt && <Span type="TIME_SINCE" spanClassName="text-xs text-500 white-space-nowrap">{chat.lastMessageAt}</Span>}
                                                            </div>
                                                            <div className="flex align-items-center gap-2 mt-1">
                                                                <span className="flex-1 text-xs text-500 overflow-hidden text-overflow-ellipsis white-space-nowrap">{preview}</span>
                                                                {participant?.unreadCount > 0 && <Badge value={participant.unreadCount} severity="danger"/>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </TabPanel>
                    </TabView>
                </div>
            </aside>

            <section className="flex-1 min-w-0 min-h-0 flex flex-column">
                {!selectedTarget ? (
                    <EmptyConversation privateMode={privateMode} hasTargets={privateMode ? privateEntries.length > 0 : groupChats.length > 0}/>
                ) : (
                    <>
                        <div className="flex align-items-center justify-content-between gap-3 px-3 py-2 border-bottom-1 border-200 surface-card">
                            <div className="flex align-items-center gap-2 min-w-0">
                                <ConversationAvatar chat={selectedChat} member={selectedPrivateMember} currentUser={user}/>
                                <div className="flex flex-column min-w-0">
                                    <strong className="text-xs overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                        {privateMode ? selectedPrivateMember?.fullname : getChatTitle(selectedChat, user)}
                                    </strong>
                                    <small className="text-xs text-500">
                                        {activeStatuses.length
                                            ? activeStatuses.map((item) => {
                                                const member = item?.user?.member || item?.user;
                                                return `${member?.firstname || member?.fullname} ${item.participantState === "sending-file" ? "در حال ارسال فایل است" : "در حال نوشتن است"}`;
                                            }).join("، ")
                                            : privateMode ? "گفتگوی خصوصی" : `${selectedChat?.users?.length || 0} عضو`}
                                    </small>
                                </div>
                            </div>
                            <div className="flex flex-wrap align-items-center justify-content-end gap-2">
                                <IconField className="align-items-center flex w-16rem max-w-full">
                                    <InputIcon className={messageSearchLoading ? "pi pi-spin pi-spinner" : "pi pi-search"}/>
                                    <InputText
                                        value={messageSearch}
                                        onChange={(event) => setMessageSearch(event.target.value)}
                                        disabled={!selectedChat}
                                        className="w-full text-xs"
                                        placeholder="جستجو در پیام‌ها"
                                        aria-label="جستجو در پیام‌ها"
                                    />
                                </IconField>
                                <Dropdown
                                    value={messageKind}
                                    options={MESSAGE_KIND_OPTIONS}
                                    onChange={(event) => setMessageKind(event.value ?? null)}
                                    placeholder="همه پیام‌ها"
                                    showClear
                                    disabled={!selectedChat}
                                    className="text-xs conversation-header-filter"
                                    aria-label="فیلتر محتوای گفتگو"
                                />
                                <Dropdown
                                    value={messageDate}
                                    options={MESSAGE_DATE_OPTIONS}
                                    onChange={(event) => setMessageDate(event.value ?? null)}
                                    placeholder="تاریخ پیام‌ها"
                                    showClear
                                    disabled={!selectedChat}
                                    className="text-xs conversation-header-filter"
                                    aria-label="فیلتر تاریخ پیام‌ها"
                                />
                                {messageFiltersActive && !messageSearchLoading && (
                                    <small className="text-xs text-500 white-space-nowrap">{messageSearchTotal} نتیجه</small>
                                )}
                                {canManageGroup && (
                                    <>
                                    <Button
                                        label="دعوت عضو"
                                        icon={<Icon icon="mi mi:person_add" className="text-sm"/>}
                                        className="text-xs gap-1 p-2 p-button-secondary p-button-outlined"
                                        onClick={() => { setDialogError(""); setInviteMembers([]); setInviteVisible(true); }}
                                    />
                                    <Button
                                        label="ویرایش گروه"
                                        icon={<Icon icon="mi mi:edit" className="text-sm"/>}
                                        className="text-xs gap-1 p-2 p-button-secondary p-button-outlined"
                                        onClick={openEditGroup}
                                    />
                                    <Button
                                        label="حذف گروه"
                                        icon={<Icon icon="mi mi:delete" className="text-sm"/>}
                                        severity="danger"
                                        outlined
                                        className="text-xs gap-1 p-2"
                                        onClick={removeGroup}
                                    />
                                    </>
                                )}
                            </div>
                        </div>

                        <div
                            ref={messageListRef}
                            className="flex-1 min-h-0 overflow-auto p-3 surface-ground"
                            onScroll={handleMessageListScroll}
                        >
                            {loadingOlderMessages && (
                                <div className="flex align-items-center justify-content-center py-2">
                                    <ProgressSpinner style={{width: "1.5rem", height: "1.5rem"}} strokeWidth="4"/>
                                </div>
                            )}
                            {messagesLoading && !messages.length ? (
                                <div className="h-full flex align-items-center justify-content-center">
                                    <ProgressSpinner style={{width: "2rem", height: "2rem"}} strokeWidth="4"/>
                                </div>
                            ) : messageSearchLoading && !visibleMessages.length ? (
                                <div className="h-full flex align-items-center justify-content-center">
                                    <ProgressSpinner style={{width: "2rem", height: "2rem"}} strokeWidth="4"/>
                                </div>
                            ) : !visibleMessages.length ? (
                                <div className="h-full flex flex-column align-items-center justify-content-center text-500">
                                    <Icon icon={messageFiltersActive ? "mi mi:search_off" : "mi mi:send"} className="text-3xl text-300 mb-2"/>
                                    <span className="text-xs">
                                        {messageFiltersActive ? "پیامی با این فیلترها پیدا نشد." : "اولین پیام این گفتگو را ارسال کنید."}
                                    </span>
                                </div>
                            ) : messageGroups.map((group) => (
                                <section key={group.key} className="inbox-message-day-group">
                                    <div className="inbox-message-day-separator" role="separator" aria-label={group.label}>
                                        <span>{group.label}</span>
                                    </div>
                                    {group.messages.map((message) => (
                                        <ChatMessage
                                            key={entityKey(message)}
                                            item={message}
                                            seen={seenMessageKeys.has(String(entityKey(message)))}
                                            showSender={selectedChat?.type === "group"}
                                            currentMembership={workspaceMembershipData}
                                            reactionLoading={reactingMessageKey === entityKey(message)}
                                            onReact={handleMessageReaction}
                                            onAttachmentError={handleAttachmentError}
                                            onEdit={(item) => chooseMessageAction("edit", item)}
                                            onDelete={removeMessage}
                                            onReply={(item) => chooseMessageAction("reply", item)}
                                            onOpenReference={handleOpenReference}
                                            onTextContextMenu={handleMessageTextContextMenu}
                                        />
                                    ))}
                                </section>
                            ))}
                        </div>

                        <div className="surface-card border-top-1 border-200">
                            {actionMessage && (
                                <div className="flex align-items-center justify-content-between gap-3 px-3 py-2 surface-50 border-bottom-1 border-100">
                                    <div className="min-w-0">
                                        <Tag value={actionMessage.action === "edit" ? "ویرایش پیام" : "پاسخ به پیام"} severity="info" className="mb-1 text-xs"/>
                                        <div className="text-xs text-600 overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                            {actionMessage.message?.messageContent
                                                || actionMessage.message?.attachment?.[0]?.name
                                                || valueList(actionMessage.message?.linkedEntity)?.[0]?.title}
                                        </div>
                                    </div>
                                    <Button
                                        icon={<Icon icon="mi mi:close" className="text-sm"/>}
                                        rounded text severity="secondary"
                                        className="text-xs w-2rem h-2rem p-0"
                                        onClick={() => {
                                            setActionMessage(null);
                                            setMessageText("");
                                            setMessageMentions([]);
                                            setLinkedEntities([]);
                                            setPendingAttachments([]);
                                        }}
                                    />
                                </div>
                            )}
                            <div className="p-3">
                                <ChatComposer
                                    value={messageText}
                                    members={availableMentionMembers}
                                    references={referenceSuggestions}
                                    referencesLoading={referenceDataLoading}
                                    attachments={pendingAttachments}
                                    onChange={handleComposerChange}
                                    onSelect={handleComposerSelect}
                                    onRemoveAttachment={(attachment) => setPendingAttachments((current) => (
                                        current.filter((item) => !sameEntity(item, attachment))
                                    ))}
                                    canAttach={actionMessage?.action !== "edit"}
                                    onAttach={openAttachmentUpload}
                                    onSubmit={submitComposer}
                                    submitDisabled={!messageText.trim() && !linkedEntities.length && !pendingAttachments.length}
                                    loading={sending}
                                    disabled={sending}
                                    placeholder={translate("inbox.composer.placeholder-with-help")}
                                    onFocus={() => setChatStatus("typing")}
                                    onBlur={() => setChatStatus("")}
                                />
                            </div>
                        </div>
                    </>
                )}
            </section>

            <Dialog
                pt={{
                    footer: () => ({className: "p-0 border-round-bottom-2xl"}),
                    content: () => ({className: "p-0 h-full"}),
                }}
                header={<DialogHeader title="ایجاد گروه" description="نام و اعضای گروه را مشخص کنید"/>}
                contentClassName="p-4"
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                footer={createGroupFooter}
                className="rtl w-6 border-round-bottom-2xl"
                visible={createGroupVisible}
                onHide={() => setCreateGroupVisible(false)}
            >
                <div className="flex flex-column gap-3 text-xs">
                    <div className="flex flex-column gap-2">
                        <label htmlFor="new-group-title" className="text-xs font-medium">نام گروه</label>
                        <IconField className="align-items-center flex w-full">
                            <InputIcon className="pi pi-users"/>
                            <InputText
                                id="new-group-title"
                                value={groupTitle}
                                onChange={(event) => setGroupTitle(event.target.value)}
                                className="w-full text-xs"
                            />
                        </IconField>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="text-xs font-medium">اعضای گروه</label>
                        <MemberMultiSelect
                            value={groupMembers}
                            options={workspaceMembers}
                            onChange={setGroupMembers}
                            placeholder="اعضا را انتخاب کنید"
                        />
                    </div>
                    {dialogError && <small className="text-red-500 text-xs">{dialogError}</small>}
                </div>
            </Dialog>

            <Dialog
                pt={{
                    footer: () => ({className: "p-0 border-round-bottom-2xl"}),
                    content: () => ({className: "p-0 h-full"}),
                }}
                header={<DialogHeader title="ویرایش گروه" description="نام، توضیحات و اعضای گروه را ویرایش کنید"/>}
                contentClassName="p-4"
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                footer={editGroupFooter}
                className="rtl w-6 border-round-bottom-2xl"
                visible={editGroupVisible}
                onHide={() => setEditGroupVisible(false)}
            >
                <div className="flex flex-column gap-3 text-xs">
                    <div className="flex flex-column gap-2">
                        <label htmlFor="edit-group-title" className="text-xs font-medium">نام گروه</label>
                        <IconField className="align-items-center flex w-full">
                            <InputIcon className="pi pi-users"/>
                            <InputText
                                id="edit-group-title"
                                value={editGroupTitle}
                                onChange={(event) => setEditGroupTitle(event.target.value)}
                                className="w-full text-xs"
                            />
                        </IconField>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="edit-group-description" className="text-xs font-medium">توضیحات</label>
                        <InputTextarea
                            id="edit-group-description"
                            value={editGroupDescription}
                            onChange={(event) => setEditGroupDescription(event.target.value)}
                            rows={3}
                            autoResize
                            className="w-full text-xs"
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="text-xs font-medium">اعضای گروه</label>
                        <MemberMultiSelect
                            value={editGroupMembers}
                            options={workspaceMembers}
                            onChange={setEditGroupMembers}
                            placeholder="اعضا را انتخاب کنید"
                        />
                    </div>
                    {dialogError && <small className="text-red-500 text-xs">{dialogError}</small>}
                </div>
            </Dialog>

            <Dialog
                pt={{
                    footer: () => ({className: "p-0 border-round-bottom-2xl"}),
                    content: () => ({className: "p-0 h-full"}),
                }}
                header={<DialogHeader title="دعوت اعضا به گروه" description={getChatTitle(selectedChat, user)}/>}
                contentClassName="p-4"
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                footer={inviteFooter}
                className="rtl w-6 border-round-bottom-2xl"
                visible={inviteVisible}
                onHide={() => setInviteVisible(false)}
            >
                <div className="flex flex-column gap-3 text-xs">
                    {inviteOptions.length ? (
                        <div className="flex flex-column gap-2">
                            <label className="text-xs font-medium">اعضای جدید</label>
                            <MemberMultiSelect
                                value={inviteMembers}
                                options={inviteOptions}
                                onChange={setInviteMembers}
                                placeholder="اعضا را انتخاب کنید"
                            />
                        </div>
                    ) : (
                        <div className="text-center text-500 p-3 text-xs">همه اعضای فضای کاری در این گروه حضور دارند.</div>
                    )}
                    {dialogError && <small className="text-red-500 text-xs">{dialogError}</small>}
                </div>
            </Dialog>

            <ContextMenu
                ref={messageContextMenuRef}
                model={messageContextMenuItems}
                dir="rtl"
                className="inbox-message-context-menu rtl text-xs text-right"
                pt={{
                    menu: () => ({dir: "rtl", className: "rtl text-right"}),
                    menuitem: () => ({className: "text-xs rtl text-right"}),
                    action: () => ({dir: "rtl", className: "rtl text-right"}),
                    label: () => ({className: "text-right"}),
                }}
            />

            <TaskDialog
                visible={createFromMessage?.type === "task"}
                setVisible={(visible) => { if (!visible) setCreateFromMessage(null); }}
                payload={{taskData: {title: createFromMessage?.title || ""}}}
                onSubmit={createTaskFromMessage}
            />

            <DeliverableDialog
                visible={createFromMessage?.type === "deliverable"}
                setVisible={(visible) => { if (!visible) setCreateFromMessage(null); }}
                payload={{deliverableData: {title: createFromMessage?.title || ""}}}
                onSubmit={createDeliverableFromMessage}
            />

            <CreateProjectDialog
                visible={createFromMessage?.type === "project"}
                setVisible={(visible) => { if (!visible) setCreateFromMessage(null); }}
                initialData={{title: createFromMessage?.title || ""}}
                onSubmit={createProjectFromMessage}
            />

            <TaskSidebar
                selectedTask={selectedReferenceTask}
                setSelectedTask={setSelectedReferenceTask}
                updateTask={updateReferenceTask}
            />

            <GoalSidebar
                goalData={selectedReferenceGoal}
                setGoalData={setSelectedReferenceGoal}
            />

            <ProjectSidebar
                projectData={selectedReferenceProject}
                setProjectData={setSelectedReferenceProject}
                updateProject={updateReferenceProject}
            />

            <DeliverableSidebar
                selectedDeliverable={selectedReferenceDeliverable}
                setSelectedDeliverable={setSelectedReferenceDeliverable}
                tasks={tasks || []}
                updateTask={updateReferenceTask}
                onSubmit={updateReferenceDeliverable}
            />

            <ConfirmDialog
                group="delete-chat-message"
                className="rtl w-20rem"
                acceptClassName="text-xs p-button-danger gap-1"
                rejectClassName="text-xs p-button-danger p-button-text gap-1"
            />

            <ConfirmDialog
                group="delete-chat-group"
                className="rtl w-20rem"
                acceptClassName="text-xs p-button-danger gap-1"
                rejectClassName="text-xs p-button-danger p-button-text gap-1"
            />

            <UploadDialog
                visible={uploadVisible}
                setVisible={(visible) => {
                    setUploadVisible(visible);
                    if (!visible) {
                        setUploadChatKey(null);
                        setChatStatus("");
                    }
                }}
                multiple
                entityType="chat"
                entityDocumentId={uploadChatKey}
                purpose="attachment"
                updateContentType={(files) => setPendingAttachments((current) => {
                    const existing = new Set(current.map((item) => String(entityKey(item))));
                    return [...current, ...files.filter((item) => !existing.has(String(entityKey(item))))];
                })}
            />
        </div>
    );
}

export default ConversationsTab;
